'use client'
import { motion } from 'motion/react'
import Image from 'next/image'
import type { FC } from 'react'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

/**
 * Customer proof directly under the hero: the real deployment numbers, one line
 * that ties them to the no-media-player proposition, and the customers running
 * it. Content lives in `content/homepage.json`. A customer gets a wordmark
 * until a `logo` path is supplied with permission to use it.
 */
export interface TrustBarSlice extends SliceLike<'trust_bar'> {
  variation: 'default'
  primary: {
    eyebrow?: string
    title?: string
    stats: { value: string; label: string }[]
    statement?: string
    customers_label?: string
    customers?: { name: string; logo?: string; href?: string }[]
  }
}

export type TrustBarProps = SliceComponentProps<TrustBarSlice>

const TrustBar: FC<TrustBarProps> = ({ slice }) => {
  const { eyebrow, title, stats, statement, customers_label, customers } = slice.primary
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white pt-16 pb-16 md:pt-24 md:pb-20"
    >
      <div className="container">
        {(eyebrow || title) && (
          <div className="max-w-[720px]">
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            {title && <h2 className="display-title mt-3">{title}</h2>}
          </div>
        )}

        <motion.dl
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-12 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4"
        >
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col border-l border-black/10 pl-5">
              <dd className="font-rubik text-ink order-1 text-[26px] leading-[1.05] font-semibold tracking-tight text-balance md:text-[40px]">
                {s.value}
              </dd>
              <dt className="order-2 mt-3 max-w-[22ch] text-[14px] leading-5 text-[#4b4b4b] md:text-[15px]">
                {s.label}
              </dt>
            </div>
          ))}
        </motion.dl>

        {statement && (
          <p className="font-rubik text-ink mt-12 max-w-[34ch] text-[22px] leading-[1.3] font-medium tracking-tight md:text-[28px]">
            {statement}
          </p>
        )}
      </div>

      {customers && customers.length > 0 && (
        <div className="mt-12 border-y border-black/5 py-5">
          <div className="container flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-8">
            {customers_label && (
              <p className="shrink-0 text-[12px] font-semibold tracking-[0.14em] text-[#6b6b6b] uppercase">
                {customers_label}
              </p>
            )}
            <ul className="flex flex-wrap items-center gap-x-10 gap-y-3">
              {customers.map((c) => {
                const mark = c.logo ? (
                  <Image
                    src={c.logo}
                    alt={c.name}
                    width={160}
                    height={40}
                    className="h-8 w-auto opacity-80 grayscale transition hover:opacity-100 hover:grayscale-0"
                  />
                ) : (
                  <span className="font-rubik text-[22px] font-semibold tracking-tight text-[#2d033b]/80 transition hover:text-[#2d033b]">
                    {c.name}
                  </span>
                )
                return (
                  <li key={c.name} className="flex items-center">
                    {c.href ? (
                      <a href={c.href} aria-label={`${c.name} case study`}>
                        {mark}
                      </a>
                    ) : (
                      mark
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </section>
  )
}

export default TrustBar
